import React, { useCallback, useRef } from 'react';
import {
  Type, AlignLeft, List, CheckSquare, Circle, Calendar, Upload, Hash, Mail,
  Phone, Link, Heading, FileText, SlidersHorizontal, BarChart3, Star,
  Image, ToggleLeft, MessageSquare, Sparkles, PartyPopper, Settings, ExternalLink,
} from 'lucide-react';
import type { FormField, FlowNodeData, FlowValidationIssue } from '../../types/forms';

const FIELD_ICON_MAP: Record<string, React.FC<any>> = {
  text: Type, email: Mail, phone: Phone, textarea: AlignLeft,
  select: List, checkbox: CheckSquare, radio: Circle, multiselect: List,
  date: Calendar, datetime: Calendar, time: Calendar, file: Upload,
  number: Hash, url: Link, heading: Heading, paragraph: FileText,
  opinion_scale: SlidersHorizontal, nps: BarChart3, rating: Star,
  picture_choice: Image, yes_no: ToggleLeft, statement: MessageSquare,
  welcome_screen: Sparkles, thank_you_screen: PartyPopper,
  redirect_url: ExternalLink,
};

interface FlowNodeProps {
  field: FormField;
  node: FlowNodeData;
  isSelected: boolean;
  validationIssues: FlowValidationIssue[];
  outgoingCount: number;
  onSelect: (fieldId: string) => void;
  onMove: (fieldId: string, position: { x: number; y: number }) => void;
  onStartConnection: (fieldId: string, e: React.MouseEvent) => void;
  onOpenRules: (fieldId: string) => void;
  zoom: number;
}

export const FlowNode: React.FC<FlowNodeProps> = ({
  field,
  node,
  isSelected,
  validationIssues,
  outgoingCount,
  onSelect,
  onMove,
  onStartConnection,
  onOpenRules,
  zoom,
}) => {
  const dragStartPos = useRef<{ x: number; y: number; nodeX: number; nodeY: number } | null>(null);

  const isDeadEnd = validationIssues.some(i => i.type === 'dead_end' && i.fieldIds.includes(field.id));
  const isLoop = validationIssues.some(i => i.type === 'loop' && i.fieldIds.includes(field.id));
  const isUnreachable = validationIssues.some(i => i.type === 'unreachable' && i.fieldIds.includes(field.id));

  const borderColor = isLoop
    ? 'border-red-500'
    : isDeadEnd
    ? 'border-amber-500'
    : isSelected
    ? 'border-primary-500 ring-2 ring-primary-500/30'
    : 'border-neutral-700/50';

  const Icon = FIELD_ICON_MAP[field.type] || Type;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-port]') || (e.target as HTMLElement).closest('[data-node-action]')) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(field.id);

    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      nodeX: node.position.x,
      nodeY: node.position.y,
    };

    const handleMouseMove = (me: MouseEvent) => {
      if (!dragStartPos.current) return;
      const dx = (me.clientX - dragStartPos.current.x) / zoom;
      const dy = (me.clientY - dragStartPos.current.y) / zoom;
      onMove(field.id, {
        x: dragStartPos.current.nodeX + dx,
        y: dragStartPos.current.nodeY + dy,
      });
    };

    const handleMouseUp = () => {
      dragStartPos.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [field.id, node.position, onMove, onSelect, zoom]);

  return (
    <div
      className={`absolute select-none cursor-grab active:cursor-grabbing ${isUnreachable ? 'opacity-50' : ''}`}
      style={{
        left: node.position.x,
        top: node.position.y,
        width: node.width,
        zIndex: isSelected ? 30 : 20,
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={(e) => { e.stopPropagation(); onOpenRules(field.id); }}
      data-node
    >
      {/* Input port (left center) */}
      <div
        className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-neutral-700 border-2 border-neutral-600 z-10 hover:bg-primary-500 hover:border-primary-400 transition-colors"
        data-port="input"
        data-field-id={field.id}
      />

      {/* Node card */}
      <div className={`liquid-glass rounded-xl px-4 py-3 border-2 transition-all group ${borderColor}`}>
        <div className="flex items-center gap-2.5">
          <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-neutral-800/80 flex items-center justify-center">
            <Icon className="w-3.5 h-3.5 text-neutral-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{field.label}</p>
            <p className="text-xs text-neutral-500">{field.type}</p>
          </div>
          {field.required && (
            <span className="text-red-400 text-xs font-bold">*</span>
          )}
          <button
            data-node-action
            onClick={(e) => { e.stopPropagation(); onOpenRules(field.id); }}
            className="p-1 rounded-md text-neutral-500 hover:text-primary-400 hover:bg-primary-500/10 transition-colors opacity-0 group-hover:opacity-100"
            title="Editar regras"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Badges */}
        <div className="flex items-center gap-1.5 mt-1.5">
          {outgoingCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-500/20 text-primary-400">
              {outgoingCount} {outgoingCount !== 1 ? 'condições' : 'condição'}
            </span>
          )}
          {isLoop && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">
              Loop
            </span>
          )}
          {isDeadEnd && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
              Sem saída
            </span>
          )}
        </div>
      </div>

      {/* Output port (right center) — hidden for terminal nodes */}
      {field.type !== 'redirect_url' && (
        <div
          className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-neutral-700 border-2 border-neutral-600 z-10 cursor-crosshair hover:bg-primary-500 hover:border-primary-400 transition-colors"
          data-port="output"
          data-field-id={field.id}
          onMouseDown={(e) => {
            e.stopPropagation();
            onStartConnection(field.id, e);
          }}
        />
      )}
    </div>
  );
};
